import { apiClient } from '$lib/api/client';
import { handleError } from '$lib/utils/error-handling';
import { notifications } from '$lib/stores/notifications';
import { selectedIndustryCentroids } from '$lib/stores/data';
import {
  embeddingQuery,
  queryFilters,
  queryResult,
  queryRunning,
  resultStats,
  selectionScope,
  textConstraints,
  scoreFilterRange
} from '$lib/stores/selection';
import { applicationMode } from '$lib/stores/mode';
import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';

import { listHasGone } from '$lib/api/lists';
import type { Contact, CompanyWithContacts, FilterResponse, Filters } from '$lib/types/api';
import {
  mixedResults,
  hasMixedSources,
  setOriginalResults,
  getAllCompanyIds,
  updateFilteredResults
} from '$lib/stores/mixedResults';
import { fetchSelection } from '$lib/api/selection';
import { resetHydration } from '$lib/stores/hydration';
import { track } from '$lib/analytics/tracking';
import { turnstile } from '$lib/turnstile';
import { pixel } from '$lib';

interface FilterRequest {
  filters: Filters;
  data_pool_ids: number[];
  contact_mode?: 'single' | 'multiple';
  turnstile_token?: string;
}

// Helper function to assemble companies with their contacts
export function assembleCompaniesWithContacts(response: FilterResponse): CompanyWithContacts[] {
  const { companies, contacts } = response;

  // Ensure contacts is always an array (handle null, undefined, or missing)
  const contactsArray = contacts || [];

  // Group contacts by company ID
  const contactsByCompany = contactsArray.reduce(
    (acc, contact) => {
      if (!acc[contact.company_id]) {
        acc[contact.company_id] = [];
      }
      acc[contact.company_id].push(contact);
      return acc;
    },
    {} as Record<string, Contact[]>
  );

  // Attach contacts to companies
  const result = companies.map((company) => ({
    ...company,
    contacts: contactsByCompany[company.id] || []
  }));

  return result;
}

export async function submitFilters(): Promise<void> {
  try {
    // Read what is held before clearing it.
    //
    // These four lines used to come after the reset below, which meant they
    // read the empty state it had just written: `hasMixed` was always false
    // and `currentMixedResults` always empty, so the narrow-within branch
    // could not be reached at all. Re-filtering a selection with lookalikes in
    // it quietly ran a fresh search and answered with the whole corpus.
    const filters = get(queryFilters);
    const centroids = Array.from(get(selectedIndustryCentroids).keys());
    const currentMixedResults = get(mixedResults);
    const hasMixed = get(hasMixedSources);

    // Likewise read before the reset: `getAllCompanyIds` reads the same store,
    // so asking for them afterwards answers with none.
    const mixedIds =
      hasMixed &&
      (currentMixedResults.companies.length > 0 || currentMixedResults.similarityBatches.length > 0)
        ? getAllCompanyIds()
        : null;

    queryRunning.set(true);
    queryResult.set([]);
    resultStats.set({});
    scoreFilterRange.set([0, 100]); // Reset score filter to full range

    // Also reset mixed results to ensure no data is displayed while loading
    setOriginalResults([]);

    // Validate request
    if (!filters || typeof filters !== 'object') {
      throw new Error('Invalid filters provided');
    }

    /*
     * Which companies this may draw from, when the filters cannot say.
     *
     * Two selections cannot be reproduced by asking the search again, and both
     * have to be narrowed by naming their members instead:
     *
     * A *scoped* one — a saved list loaded as a selection, or a full-scope
     * search that answered a phrase living in no filter. `selectionScope` holds
     * what was seated, so every submit re-narrows the whole set and clearing a
     * filter restores what it excluded. Loading a list and then filtering ran
     * an ordinary search before this, which threw the list away and answered
     * with the whole corpus — the filter panel appeared to do nothing to it.
     *
     * A *mixed* one — lookalike results sitting beside a filter search. Those
     * came from a vector search around one company, and the batches grow after
     * seating, so the current identifiers are the truthful set.
     */
    const scope = get(selectionScope);
    const within = scope?.length ? scope : mixedIds;

    if (within) {
      // The answer is an index, and the rows follow a block at a time — the
      // same two-part fetch a fresh search uses, for the same reason.
      const companyIds = within;

      resetHydration();
      const narrowed = await fetchSelection(companyIds);

      if (narrowed.companies.length > 0) {
        updateFilteredResults(narrowed.companies);

        // Both stores, or the screen throws the answer away.
        //
        // This branch wrote only `mixedResults`, and `queryResult` was left as
        // the empty array the reset above put there. Results.svelte watches
        // `queryResult` and, once the search stops running, calls
        // setOriginalResults with it — so the narrowed set was wiped a tick
        // after it arrived, and the list drew nothing. It never showed while
        // the branch was unreachable.
        //
        // After updateFilteredResults, so that a selection with lookalikes in
        // it keeps the metadata that call rebuilds: the reset in Results is
        // skipped while there are similarity batches, and by now there are.
        queryResult.set(narrowed.companies);

        const companyCount = narrowed.companies.length;
        const contactCount = narrowed.companies.reduce((n, c) => n + c.contacts.length, 0);

        resultStats.set({
          company_count: narrowed.total,
          contact_count: contactCount,
          capped: narrowed.capped
        });
        notifications.success(
          `Filtered ${companyCount} ${companyCount === 1 ? 'company' : 'companies'} with ${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'}`
        );
      } else {
        updateFilteredResults([]);
        queryResult.set([]);
        resultStats.set({});
        notifications.info('No companies match the current filters');
      }
    } else {
      // A fresh search, which replaces the selection rather than narrowing it.
      // Whatever scope a list or a phrase had put there is gone with it — the
      // set about to arrive is described by the filters and is reproducible
      // from them.
      selectionScope.set(null);

      // Either way of naming a person is a criterion. Knowing only about the
      // roles, this threw "Please set at least one filter" at a customer who
      // had chosen job titles — before any request was made, which is why the
      // network showed nothing but the centroid listing.
      const hasRoles =
        (filters.contact_roles && filters.contact_roles.length > 0) ||
        (filters.contact_titles && filters.contact_titles.length > 0);
      const hasEmbeddingQuery =
        filters.embedding_query && filters.embedding_query.trim().length > 0;
      const hasFilters =
        (filters.cantons && filters.cantons.length > 0) ||
        (filters.postal_codes && filters.postal_codes.length > 0) ||
        (filters.postal_radius && filters.postal_radius.center_postal_code) ||
        (filters.languages && filters.languages.length > 0) ||
        (filters.text_constraints && filters.text_constraints.length > 0) ||
        (filters.company_size_range &&
          filters.company_size_range.some((v: number | null) => v !== null)) ||
        (filters.founding_year_range &&
          filters.founding_year_range.some((v: number | null) => v !== null)) ||
        isSet(filters.structured_filters) ||
        Boolean(filters.signal_filters?.selected?.length);
      if (centroids.length === 0 && !hasRoles && !hasEmbeddingQuery && !hasFilters) {
        throw new Error(
          'Please set at least one filter, embedding query, or contact role to search.'
        );
      }

      // The selection arrives as an index and its rows follow a block at a
      // time, because a preview row is 827 bytes and a broad search matches
      // hundreds of thousands of companies. See $lib/api/selection.
      //
      // The Turnstile token is obtained inside fetchSelection rather than
      // here: a token is single-use, so one cannot be fetched in advance and
      // then spent by a call that asks for its own.
      resetHydration();
      const selection = await fetchSelection();

      if (selection.companies.length > 0) {
        queryResult.set(selection.companies);
        resultStats.set({
          company_count: selection.total,
          contact_count: selection.companies.reduce((n, c) => n + c.contacts.length, 0),
          capped: selection.capped
        });

        const companyCount = selection.total;
        const contactCount = selection.companies.reduce((n, c) => n + c.contacts.length, 0);
        notifications.success(
          `Found ${companyCount} ${companyCount === 1 ? 'company' : 'companies'} with ${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'}`
        );

        // Track successful search
        track.searchPerformed({
          mode: get(applicationMode),
          filters: filters,
          resultCount: companyCount,
          companyCount: companyCount,
          contactCount: contactCount
        });

        // Track first search conversion with Meta Pixel
        if (typeof window !== 'undefined' && !sessionStorage.getItem('first_search_tracked')) {
          pixel.trackConversion('Search', {
            search_type: 'filter',
            company_count: companyCount,
            contact_count: contactCount,
            mode: get(applicationMode),
            // Filter details
            has_postal_codes: !!(filters.postal_codes && filters.postal_codes.length > 0),
            postal_codes_count: filters.postal_codes?.length || 0,
            has_cantons: !!(filters.cantons && filters.cantons.length > 0),
            cantons_count: filters.cantons?.length || 0,
            has_company_size: !!(
              filters.company_size_range && filters.company_size_range.some((v) => v !== null)
            ),
            has_contact_roles: !!(filters.contact_roles && filters.contact_roles.length > 0),
            contact_roles_count: filters.contact_roles?.length || 0,
            has_founding_year: !!(
              filters.founding_year_range && filters.founding_year_range.some((v) => v !== null)
            ),
            has_ai_search: !!filters.embedding_query,
            industry_segments: centroids.length,
            industry_segment_ids: JSON.stringify(centroids)
          });
          sessionStorage.setItem('first_search_tracked', 'true');
        }
      } else {
        queryResult.set([]);
        resultStats.set({});
        notifications.info('No companies found matching your criteria');

        // Track search with no results
        track.searchPerformed({
          mode: get(applicationMode),
          filters: filters,
          resultCount: 0,
          companyCount: 0,
          contactCount: 0
        });
      }
    }
  } catch (error) {
    // The customer's list expired between choosing it and searching with it.
    // The service refuses rather than quietly running the search unsubtracted,
    // so this is the app's job: let the code go and say what happened, without
    // dressing it up as a failed search.
    if (listHasGone(error)) {
      notifications.error(get(_)('customerList.gone'));
      return;
    }

    const errorMessage = await handleError(error, {
      operation: 'submitFilters',
      component: 'filters-api'
    });

    notifications.error(errorMessage);
    queryResult.set([]);
    resultStats.set({});
  } finally {
    queryRunning.set(false);
  }
}

export async function aiRefine(): Promise<void> {
  try {
    queryRunning.set(true);
    const previousResult = get(queryResult);
    const previousResultCount = previousResult ? previousResult.length : 0;
    queryResult.set([]);
    resultStats.set({});

    // Also reset mixed results to ensure no data is displayed while loading
    setOriginalResults([]);

    const filters = get(queryFilters);
    const centroids = Array.from(get(selectedIndustryCentroids).keys());
    const aiQuery = get(embeddingQuery);

    // Get Turnstile token (with timeout to prevent hanging)
    let turnstileToken: string | undefined;
    try {
      turnstileToken = await Promise.race([
        turnstile.getToken(),
        new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error('Turnstile timeout')), 5000)
        )
      ]);
    } catch {
      // Continue without token if it fails or times out
    }

    const request: FilterRequest = {
      filters,
      data_pool_ids: centroids.map(Number), // Convert to numbers
      contact_mode: filters.contact_mode || 'single',
      turnstile_token: turnstileToken
    };

    const response = await apiClient.post<FilterResponse>('/deep-iq/ai-refine', request);

    // Handle successful response
    if (response.data.companies && Array.isArray(response.data.companies)) {
      // Assemble companies with their contacts
      const companiesWithContacts = assembleCompaniesWithContacts(response.data);
      queryResult.set(companiesWithContacts);
      resultStats.set(response.data.stats || {});

      // Clear AI query after successful refinement
      embeddingQuery.set('');
      textConstraints.set([]);

      const companyCount = response.data.companies.length;
      const contactCount = response.data.contacts?.length || 0;
      notifications.success(
        `AI refinement complete: ${companyCount} ${companyCount === 1 ? 'company' : 'companies'} found with ${contactCount} ${contactCount === 1 ? 'contact' : 'contacts'}`
      );

      // Track AI refinement usage
      track.aiRefinementUsed({
        mode: get(applicationMode),
        query: aiQuery,
        previousResultCount: previousResultCount,
        newResultCount: companyCount
      });
    } else {
      throw new Error('Invalid response from AI refinement');
    }
  } catch (error) {
    const errorMessage = await handleError(error, {
      operation: 'aiRefine',
      component: 'filters-api'
    });

    notifications.error(errorMessage);

    // Reset state on error
    queryResult.set([]);
    resultStats.set({});
    embeddingQuery.set('');
    textConstraints.set([]);
  } finally {
    queryRunning.set(false);
  }
}

export function getDefaultFilters(): Filters {
  return {
    postal_radius: { center_postal_code: '', radius_km: 0 },
    postal_codes: [],
    cantons: [],
    languages: [],
    company_size_range: [null, null],
    founding_year_range: [null, null],
    contact_roles: [],
    contact_titles: [],
    contact_mode: 'single',
    structured_filters: {
      branch_office_count: { min: 0 },
      has_website: false,
      has_email: false,
      has_phone: false,
      has_facebook: false,
      has_linkedin: false,
      has_instagram: false,
      has_twitter: false,
      legal_form: [],
      exclude_legal_form: [],
      capital: null,
      has_revisionsverzicht: false,
      revision_company: '',
      offers_apprenticeships: false,
      is_hiring: false,
      technologies: [],
      mail_gaps: [],
      certifications: [],
      group_roles: [],
      sales_models: []
    },
    embedding_query: '',
    text_constraints: []
  };
}

/**
 * Whether a value says anything: a ticked flag, a non-empty list or text, a
 * number above zero, or an object holding one of those.
 *
 * What the check above asks of the structured filters, all of them. It named
 * the region, the size and the founding year and none of the rest, so a
 * selection on legal form, capital, technologies, certifications, group or
 * transport licence alone was refused here as "no filter" — before it reached
 * the API, which judges the same question itself. Asked of every field, a
 * filter added later counts without being listed.
 */
export function isSet(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return value > 0;
  if (value && typeof value === 'object') return Object.values(value).some(isSet);
  return false;
}
